import React, { useState, useMemo, useEffect } from "react";
import { ArrowLeft, CalendarDays, Check, X, Trash2, Info, ClipboardCheck, Plus, Archive, HelpCircle } from "lucide-react";
import { INK, INK_2, INK_3, LINE, BURGUNDY, NEUT_BG, BAD_INK, OK_BG, OK_INK, WARN_BG, WARN_INK } from "../Bureau/bureauTokens";
import { useEtatGlobal } from "../EtatGlobal.js";
import { httpClient } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog.js";
import TutorialOverlay from "../../components/ui/TutorialOverlay";
import congesTutorial from "../Tutoriel/CongesTutorial";

const LEAVE_TYPES = ["Congés payés", "RTT", "Récupération", "Sans solde", "Maladie"];
const STATUT_LABEL = { EN_ATTENTE: "En attente", VALIDE: "Approuvé", REFUSE: "Refusé" };
const STATUT_STYLE = {
  EN_ATTENTE: { bg: WARN_BG, ink: WARN_INK },
  VALIDE: { bg: OK_BG, ink: OK_INK },
  REFUSE: { bg: "#FBE3E3", ink: BAD_INK },
};
const REMINDERS = [
  "Programmer un message d'absence automatique sur votre messagerie.",
  "Faire remonter au responsable les dossiers et tâches en cours.",
  "Transmettre les échéances à ne pas manquer pendant votre absence.",
  "Désigner un collaborateur référent pour vos dossiers prioritaires.",
  "Clôturer vos feuilles de temps avant votre départ.",
];

function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
const DEMI_JOURNEE_LABEL = { MATIN: "matin", APRES_MIDI: "après-midi" };
function labelJours(jours, demiJournee) {
  if (demiJournee) return `demi-journée (${DEMI_JOURNEE_LABEL[demiJournee] || demiJournee})`;
  return `${jours} jour${jours > 1 ? "s" : ""}`;
}
function labelPeriode(startIso, endIso) {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const fmt = (d) => d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  return startIso === endIso ? fmt(s) : `${fmt(s)} – ${fmt(e)}`;
}

function Pill({ statut }) {
  const s = STATUT_STYLE[statut] || STATUT_STYLE.EN_ATTENTE;
  return (
    <span className="text-[10.5px] font-bold px-2 py-0.5" style={{ background: s.bg, color: s.ink, borderRadius: 20 }}>
      {STATUT_LABEL[statut] || statut}
    </span>
  );
}

function Conges({ onBack }) {
  const {
    utilisateur,
    congesDemandes: demandes,
    setCongesDemandes,
    congesAValider: aValider,
    setCongesAValider,
    congesToutes,
    setCongesToutes,
    loadingConges: isLoading,
    loadingCongesAValider: isLoadingAValider,
    loadingCongesToutes: isLoadingToutes,
  } = useEtatGlobal();
  const isAdmin = utilisateur?.role === "ADMINISTRATEUR" || utilisateur?.allCabinetsAccess;

  // Un admin voit les demandes de TOUS les collaborateurs (tous statuts) dans
  // cet onglet ; un utilisateur normal ne voit que les siennes.
  const mesListe = isAdmin ? congesToutes : demandes;
  const mesListeLoading = isAdmin ? isLoadingToutes : isLoading;

  // Un admin arrive directement sur "À valider" (c'est ce qu'il vient faire
  // en priorité ici) ; un non-admin n'a de toute façon jamais accès à cet
  // onglet (voir isAdmin && ... plus bas), donc "mes" reste son seul choix.
  const [tab, setTab] = useState(() => (isAdmin ? "avalider" : "mes"));

  const [type, setType] = useState(LEAVE_TYPES[0]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  // "" = journée(s) complète(s), sinon "MATIN" | "APRES_MIDI" — uniquement
  // proposé quand start === end (une seule journée sélectionnée).
  const [demiJournee, setDemiJournee] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);

  const flash = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  const isSingleDay = !!start && start === end;

  // La demi-journée ne s'applique qu'à une seule journée sélectionnée — si
  // la plage s'élargit à nouveau, on revient silencieusement à une journée
  // complète plutôt que de laisser une valeur qui ne correspond plus au
  // formulaire affiché.
  useEffect(() => {
    if (!isSingleDay && demiJournee) setDemiJournee("");
  }, [isSingleDay, demiJournee]);

  const nbJours = useMemo(() => {
    if (!start || !end) return 0;
    if (isSingleDay && demiJournee) return 0.5;
    const s = new Date(start);
    const e = new Date(end);
    return Math.max(0, Math.round((e - s) / 86400000) + 1);
  }, [start, end, isSingleDay, demiJournee]);

  const valid = !!start && !!end && nbJours > 0;

  const todayISO = isoOf(new Date());
  const upcoming = mesListe.filter((d) => isoOf(new Date(d.dateFin)) >= todayISO);
  const past = mesListe.filter((d) => isoOf(new Date(d.dateFin)) < todayISO);
  const enAttenteCount = mesListe.filter((d) => d.statut === "EN_ATTENTE").length;

  // Instantané : la demande apparaît tout de suite dans "Mes demandes" et le
  // formulaire se vide sans attendre la réponse serveur ; l'appel part en
  // arrière-plan et, une fois confirmé, remplace la ligne temporaire par la
  // vraie donnée et affiche le toast — en cas d'échec, la ligne est retirée
  // et une erreur s'affiche à la place.
  const submit = async () => {
    if (!valid) return;
    setError(null);
    const tempId = `temp-${Date.now()}`;
    const optimisticDemande = {
      id: tempId,
      userId: utilisateur?.id,
      user: { name: utilisateur?.name, lastName: utilisateur?.lastName },
      type,
      dateDebut: start,
      dateFin: end,
      motif: reason.trim(),
      statut: "EN_ATTENTE",
      jours: nbJours,
      demiJournee: isSingleDay && demiJournee ? demiJournee : null,
    };

    setCongesDemandes((prev) => [optimisticDemande, ...prev]);
    setCongesToutes((prev) => [optimisticDemande, ...prev]);
    setType(LEAVE_TYPES[0]);
    setStart("");
    setEnd("");
    setDemiJournee("");
    setReason("");
    setSaving(true);
    try {
      const { data } = await httpClient.post("/api/conges", {
        type: optimisticDemande.type,
        dateDebut: optimisticDemande.dateDebut,
        dateFin: optimisticDemande.dateFin,
        motif: optimisticDemande.motif,
        demiJournee: optimisticDemande.demiJournee,
      });
      setCongesDemandes((prev) => prev.map((d) => (d.id === tempId ? data.data : d)));
      setCongesToutes((prev) => prev.map((d) => (d.id === tempId ? { ...data.data, user: optimisticDemande.user } : d)));
      flash("Demande envoyée — le système est à jour");
    } catch (err) {
      setCongesDemandes((prev) => prev.filter((d) => d.id !== tempId));
      setCongesToutes((prev) => prev.filter((d) => d.id !== tempId));
      setError(err.response?.data?.message || "Erreur lors de l'envoi de la demande.");
      flash("Erreur lors de l'envoi de la demande");
    } finally {
      setSaving(false);
    }
  };

  const cancelDemande = async (id) => {
    const ok = await dialog.confirm("Annuler cette demande de congé ?");
    if (!ok) return;

    // Instantané : la demande disparaît tout de suite ; remise en place si
    // la suppression échoue côté serveur.
    const target = demandes.find((d) => d.id === id);
    const targetToutes = congesToutes.find((d) => d.id === id);
    setCongesDemandes((prev) => prev.filter((d) => d.id !== id));
    setCongesToutes((prev) => prev.filter((d) => d.id !== id));
    try {
      await httpClient.delete(`/api/conges/${id}`);
      flash("Demande annulée — le système est à jour");
    } catch (err) {
      if (target) setCongesDemandes((prev) => [target, ...prev]);
      if (targetToutes) setCongesToutes((prev) => [targetToutes, ...prev]);
      flash(err.response?.data?.message || "Erreur lors de l'annulation.");
    }
  };

  const traiter = async (id, action) => {
    const ok = await dialog.confirm(action === "valider" ? "Approuver cette demande ?" : "Refuser cette demande ?", {
      variant: action === "valider" ? "success" : "danger",
    });
    if (!ok) return;

    // Instantané : la demande sort tout de suite de la file "À valider" et
    // son statut se met à jour dans la vue "Toutes les demandes" ; remise en
    // place si le traitement échoue côté serveur.
    const target = aValider.find((d) => d.id === id);
    const nouveauStatut = action === "valider" ? "VALIDE" : "REFUSE";
    setCongesAValider((prev) => prev.filter((d) => d.id !== id));
    setCongesToutes((prev) => prev.map((d) => (d.id === id ? { ...d, statut: nouveauStatut } : d)));
    const verbe = action === "valider" ? "approuvée" : "refusée";
    try {
      const { data } = await httpClient.put(`/api/conges/${id}/${action}`);
      if (data.emailSent) {
        flash(`Demande ${verbe} — ${data.notifiedName} a été notifié·e par email`);
      } else {
        flash(`Demande ${verbe}, mais l'email de notification n'a pas pu être envoyé`);
      }
    } catch (err) {
      if (target) setCongesAValider((prev) => [target, ...prev]);
      setCongesToutes((prev) => prev.map((d) => (d.id === id ? { ...d, statut: "EN_ATTENTE" } : d)));
      flash(err.response?.data?.message || "Erreur lors du traitement.");
    }
  };

  const fieldStyle = { border: `1px solid ${LINE}`, borderRadius: 8, color: INK, background: "#fff" };

  return (
    <div className="w-full flex flex-col" style={{ minHeight: "calc(100vh - 120px)" }}>
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs mb-3 flex-shrink-0 self-start" style={{ color: INK_3 }}>
          <ArrowLeft size={14} /> Retour au Bureau
        </button>
      )}

      <div className="flex items-center gap-2.5 flex-wrap mb-4 flex-shrink-0">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold m-0" style={{ color: INK }}>
              Congés
            </h1>
            <button
              type="button"
              onClick={() => setShowTutorial(true)}
              title="Voir le tutoriel"
              className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#9B1B5D] hover:border-[#9B1B5D] transition-colors"
            >
              <HelpCircle size={14} />
            </button>
          </div>
          <p className="text-xs mt-0.5" style={{ color: INK_3 }}>
            Demandes d'absence · {utilisateur?.name} {utilisateur?.lastName}
          </p>
        </div>
        {isAdmin && (
          <>
            <span className="flex-1" />
            <div className="flex" style={{ border: `1px solid ${LINE}`, borderRadius: 9, overflow: "hidden" }}>
              <button
                onClick={() => setTab("mes")}
                className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5"
                style={{ background: tab === "mes" ? BURGUNDY : "#fff", color: tab === "mes" ? "#fff" : INK_2 }}
              >
                <CalendarDays size={12} /> Toutes les demandes
              </button>
              <button
                onClick={() => setTab("avalider")}
                className="flex items-center gap-1.5 text-[11px] font-semibold px-3 py-1.5"
                style={{ background: tab === "avalider" ? BURGUNDY : "#fff", color: tab === "avalider" ? "#fff" : INK_2, borderLeft: `1px solid ${LINE}` }}
              >
                <ClipboardCheck size={12} /> À valider {aValider.length > 0 ? `(${aValider.length})` : ""}
              </button>
            </div>
          </>
        )}
      </div>

      {tab === "avalider" ? (
        <div style={{ border: `1px solid ${LINE}`, borderRadius: 16, background: "#fff", padding: "14px 16px" }}>
          <div className="flex items-center gap-1.5 text-xs font-semibold mb-3" style={{ color: BURGUNDY }}>
            <ClipboardCheck size={14} /> Demandes en attente <span style={{ color: INK_3, fontWeight: 600 }}>({aValider.length})</span>
          </div>
          {isLoadingAValider ? (
            <div className="flex flex-col gap-1.5 animate-pulse">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5" style={{ border: `1px solid ${LINE}`, borderRadius: 8, background: "#fff" }}>
                  <div className="h-3 rounded" style={{ width: 150, background: NEUT_BG }} />
                  <div className="h-3 rounded" style={{ width: 130, background: NEUT_BG }} />
                  <div className="h-3 rounded" style={{ width: 150, background: NEUT_BG }} />
                  <div className="h-3 rounded flex-1" style={{ background: NEUT_BG }} />
                </div>
              ))}
            </div>
          ) : aValider.length === 0 ? (
            <p className="text-xs" style={{ color: INK_3 }}>
              Aucune demande en attente.
            </p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {aValider.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-[#FAF7F4]"
                  style={{ border: `1px solid ${LINE}`, borderRadius: 8, background: "#fff" }}
                >
                  <span className="text-xs font-semibold" style={{ color: INK, width: 150 }}>
                    {d.user?.name} {d.user?.lastName}
                  </span>
                  <span className="text-xs" style={{ color: INK_2, width: 130 }}>
                    {d.type}
                  </span>
                  <span className="text-xs" style={{ color: INK_3, width: 150 }}>
                    {labelPeriode(d.dateDebut, d.dateFin)} · {labelJours(d.jours, d.demiJournee)}
                  </span>
                  <span className="text-xs flex-1 truncate" style={{ color: INK_3 }}>
                    {d.motif || "—"}
                  </span>
                  <button
                    onClick={() => traiter(d.id, "valider")}
                    className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1"
                    style={{ background: OK_BG, color: OK_INK, borderRadius: 6 }}
                  >
                    <Check size={12} /> Approuver
                  </button>
                  <button
                    onClick={() => traiter(d.id, "refuser")}
                    className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1"
                    style={{ background: "#FBE3E3", color: BAD_INK, borderRadius: 6 }}
                  >
                    <X size={12} /> Refuser
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 340px" }}>
          <div className="flex flex-col gap-4">
            <div style={{ border: `1px solid ${LINE}`, borderRadius: 16, background: "#fff", padding: "14px 16px" }}>
              <div className="flex items-center gap-1.5 text-xs font-semibold mb-3" style={{ color: BURGUNDY }}>
                <CalendarDays size={14} /> {isAdmin ? "Toutes les demandes" : "Mes demandes"}
                <span className="flex-1" />
                {enAttenteCount > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5" style={{ background: WARN_BG, color: WARN_INK, borderRadius: 20 }}>
                    {enAttenteCount} en attente
                  </span>
                )}
              </div>
              {mesListeLoading ? (
                <table className="w-full text-xs animate-pulse">
                  <thead>
                    <tr style={{ color: INK_3 }}>
                      {isAdmin && <th className="text-left font-medium pb-1.5">Collaborateur</th>}
                      <th className="text-left font-medium pb-1.5">Type</th>
                      <th className="text-left font-medium pb-1.5">Période</th>
                      <th className="text-left font-medium pb-1.5">Jours</th>
                      <th className="text-left font-medium pb-1.5">Motif</th>
                      <th className="text-left font-medium pb-1.5">Statut</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${LINE}` }}>
                        {isAdmin && (
                          <td className="py-2">
                            <div className="h-3 rounded" style={{ width: 110, background: NEUT_BG }} />
                          </td>
                        )}
                        <td className="py-2">
                          <div className="h-3 rounded" style={{ width: 80, background: NEUT_BG }} />
                        </td>
                        <td className="py-2">
                          <div className="h-3 rounded" style={{ width: 70, background: NEUT_BG }} />
                        </td>
                        <td className="py-2">
                          <div className="h-3 rounded" style={{ width: 18, background: NEUT_BG }} />
                        </td>
                        <td className="py-2">
                          <div className="h-3 rounded" style={{ width: 100, background: NEUT_BG }} />
                        </td>
                        <td className="py-2">
                          <div className="h-3 rounded" style={{ width: 60, background: NEUT_BG }} />
                        </td>
                        <td className="py-2" />
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : upcoming.length === 0 ? (
                <p className="text-xs" style={{ color: INK_3 }}>
                  Aucune demande à venir.
                </p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ color: INK_3 }}>
                      {isAdmin && <th className="text-left font-medium pb-1.5">Collaborateur</th>}
                      <th className="text-left font-medium pb-1.5">Type</th>
                      <th className="text-left font-medium pb-1.5">Période</th>
                      <th className="text-left font-medium pb-1.5">Jours</th>
                      <th className="text-left font-medium pb-1.5">Motif</th>
                      <th className="text-left font-medium pb-1.5">Statut</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {upcoming.map((d) => (
                      <tr key={d.id} className="transition-colors hover:bg-[#FAF7F4]" style={{ borderTop: `1px solid ${LINE}` }}>
                        {isAdmin && (
                          <td className="py-2 font-medium" style={{ color: INK }}>
                            {d.user?.name} {d.user?.lastName}
                          </td>
                        )}
                        <td className="py-2" style={{ color: INK }}>
                          {d.type}
                        </td>
                        <td className="py-2" style={{ color: INK_2 }}>
                          {labelPeriode(d.dateDebut, d.dateFin)}
                        </td>
                        <td className="py-2" style={{ color: INK_2 }}>
                          {labelJours(d.jours, d.demiJournee)}
                        </td>
                        <td className="py-2 truncate" style={{ color: INK_3, maxWidth: 160 }}>
                          {d.motif || "—"}
                        </td>
                        <td className="py-2">
                          <Pill statut={d.statut} />
                        </td>
                        <td className="py-2 text-right">
                          {d.statut === "EN_ATTENTE" && (!isAdmin || d.userId === utilisateur?.id) && (
                            <button onClick={() => cancelDemande(d.id)} title="Annuler" style={{ color: BAD_INK }}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {past.length > 0 && (
              <div style={{ border: `1px solid ${LINE}`, borderRadius: 16, background: "#fff", padding: "14px 16px", opacity: 0.62, filter: "grayscale(1)" }}>
                <div className="flex items-center gap-1.5 text-xs font-semibold mb-3" style={{ color: INK_2 }}>
                  <Archive size={14} /> Congés passés
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ color: INK_3 }}>
                      {isAdmin && <th className="text-left font-medium pb-1.5">Collaborateur</th>}
                      <th className="text-left font-medium pb-1.5">Type</th>
                      <th className="text-left font-medium pb-1.5">Période</th>
                      <th className="text-left font-medium pb-1.5">Jours</th>
                      <th className="text-left font-medium pb-1.5">Motif</th>
                      <th className="text-left font-medium pb-1.5">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {past.map((d) => (
                      <tr key={d.id} className="transition-colors hover:bg-[#FAF7F4]" style={{ borderTop: `1px solid ${LINE}` }}>
                        {isAdmin && (
                          <td className="py-2 font-medium" style={{ color: INK }}>
                            {d.user?.name} {d.user?.lastName}
                          </td>
                        )}
                        <td className="py-2" style={{ color: INK }}>
                          {d.type}
                        </td>
                        <td className="py-2" style={{ color: INK_2 }}>
                          {labelPeriode(d.dateDebut, d.dateFin)}
                        </td>
                        <td className="py-2" style={{ color: INK_2 }}>
                          {labelJours(d.jours, d.demiJournee)}
                        </td>
                        <td className="py-2 truncate" style={{ color: INK_3, maxWidth: 160 }}>
                          {d.motif || "—"}
                        </td>
                        <td className="py-2">
                          <Pill statut={d.statut} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="self-start" style={{ position: "sticky", top: 16, border: `1px solid ${LINE}`, borderRadius: 16, background: "#fff", padding: "20px 22px" }}>
            {error && (
              <p className="text-xs mb-2" style={{ color: BAD_INK }}>
                {error}
              </p>
            )}
            <div className="flex items-center gap-1.5 text-sm font-semibold mb-4" style={{ color: BURGUNDY }}>
              <Plus size={16} /> Nouvelle demande
            </div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: INK_2 }}>
              Type d'absence
            </label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-full h-10 px-3 text-sm mb-3 appearance-none cursor-pointer outline-none" style={fieldStyle}>
              {LEAVE_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-2.5 mb-3">
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium" style={{ color: INK_2 }}>
                  Du
                </span>
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 px-3 text-sm outline-none" style={fieldStyle} />
              </label>
              <label className="flex flex-col gap-1.5">
                <span className="text-xs font-medium" style={{ color: INK_2 }}>
                  Au
                </span>
                <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} className="h-10 px-3 text-sm outline-none" style={fieldStyle} />
              </label>
            </div>
            {isSingleDay && (
              <label className="flex flex-col gap-1.5 mb-3">
                <span className="text-xs font-medium" style={{ color: INK_2 }}>
                  Durée
                </span>
                <select
                  value={demiJournee}
                  onChange={(e) => setDemiJournee(e.target.value)}
                  className="w-full h-10 px-3 text-sm appearance-none cursor-pointer outline-none"
                  style={fieldStyle}
                >
                  <option value="">Journée complète</option>
                  <option value="MATIN">Demi-journée — matin</option>
                  <option value="APRES_MIDI">Demi-journée — après-midi</option>
                </select>
              </label>
            )}
            {nbJours > 0 && (
              <div className="flex items-center gap-1.5 text-xs mb-3" style={{ color: INK_3 }}>
                <Info size={13} /> {labelJours(nbJours, isSingleDay ? demiJournee : "")} d'absence
              </div>
            )}
            <label className="block text-xs font-medium mb-1.5" style={{ color: INK_2 }}>
              Motif (facultatif)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Précisez si besoin…"
              className="w-full px-3 py-2.5 text-sm outline-none resize-y mb-4"
              style={fieldStyle}
            />
            <button
              onClick={submit}
              disabled={!valid || saving}
              className="flex items-center justify-center gap-1.5 w-full px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
              style={{ background: BURGUNDY, borderRadius: 10 }}
            >
              <Check size={15} /> {saving ? "Envoi…" : "Envoyer la demande"}
            </button>
            <div className="flex items-start gap-1.5 mt-3 text-xs" style={{ color: INK_3 }}>
              <Info size={13} className="flex-shrink-0 mt-0.5" /> La demande est transmise à un administrateur pour validation.
            </div>

            <div className="mt-5 pt-4" style={{ borderTop: `1px solid ${LINE}` }}>
              <div className="flex items-center gap-1.5 text-sm font-semibold mb-3" style={{ color: INK }}>
                <Check size={14} /> Avant de partir, pensez à…
              </div>
              <ul className="flex flex-col gap-3">
                {REMINDERS.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs" style={{ color: INK_2, lineHeight: 1.5 }}>
                    <span className="flex-shrink-0" style={{ width: 5, height: 5, borderRadius: "50%", background: BURGUNDY, marginTop: 5 }} />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {showTutorial && (
        <TutorialOverlay steps={congesTutorial} onClose={() => setShowTutorial(false)} />
      )}

      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 text-xs px-4 py-2.5 text-white z-[220]"
          style={{ background: INK, borderRadius: 10, boxShadow: "0 12px 32px rgba(0,0,0,.24)" }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

export default Conges;
